<template>
  <v-dialog v-model="studentWheelStore.modalOpen" fullscreen scrim="white" class="student-wheel-dialog">
    <div class="student-wheel-overlay" data-testid="StudentWheelModal-overlay">
      <div class="student-wheel-toolbar">
        <div class="student-wheel-toolbar-row">
          <v-select
            v-model="selectedJournalId"
            variant="outlined"
            placeholder="Izvēlēties žurnālu"
            menu-icon="fa-solid fa-chevron-down"
            hide-details
            :items="journalOptions"
            :loading="loadingJournals"
            class="student-wheel-journal-select"
            data-testid="StudentWheelModal-journalSelect"
          />

          <v-btn
            variant="flat"
            rounded="pill"
            class="student-wheel-spin-btn px-4 font-weight-bold"
            color="yellowBack"
            :disabled="wheelItems.length === 0 || isSpinning"
            data-testid="StudentWheelModal-spin"
            @click="spin"
          >
            Izsaukt
          </v-btn>
        </div>

        <div class="student-wheel-toolbar-separator">
          <span class="line" />
          <span class="text">vai</span>
          <span class="line" />
        </div>

        <v-textarea
          v-model="customNames"
          variant="outlined"
          placeholder="Ievadi vārdus katru jaunā rindā"
          hide-details
          rows="6"
          no-resize
          class="student-wheel-custom-names"
          data-testid="StudentWheelModal-customNames"
        />
      </div>

      <div class="student-wheel-center" @click="spin">
        <div class="student-wheel-pointer-top" />
        <div ref="wheelContainer" data-testid="StudentWheelModal-wheel" class="student-wheel-container" />
        <div v-if="selectedStudent" class="student-wheel-result" data-testid="StudentWheelModal-result">
          <span class="student-wheel-result-name">{{ selectedStudent }}</span>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script lang="ts">
import confetti from "canvas-confetti"
import { Wheel } from "spin-wheel"

import { buildUniqueNameLabels, findFullName } from "~/helpers/uniqueNameLabels"
import storage from "~/services/storage"
import { useEklaseStore } from "~/stores/eklaseStore"
import { useStudentWheelStore } from "~/stores/studentWheelStore"

const WHEEL_COLORS = [
  "#F9D805", "#FFF1AE",
]

const DEFAULT_ITEMS_COUNT = 20

function buildPlaceholderItems() {
  return Array.from({ length: DEFAULT_ITEMS_COUNT }, () => ({ label: "" }))
}

export default {
  setup() {
    const eklaseStore = useEklaseStore()
    const studentWheelStore = useStudentWheelStore()
    return { eklaseStore, studentWheelStore }
  },

  data() {
    return {
      selectedJournalId: null as number | null,
      customNames: "",
      loadingJournals: true,
      isSpinning: false,
      selectedStudent: null as string | null,
      wheelInstance: null as Wheel | null,
      scrimClipObserver: null as ResizeObserver | null,
      clipScrimHandler: null as (() => void) | null,
    }
  },

  computed: {
    journalOptions(): { title: string; value: number }[] {
      return this.eklaseStore.journals.map((journal) => ({
        title: journal.eklase_name,
        value: journal.id,
      }))
    },

    students() {
      const journal = this.eklaseStore.journals.find((j) => j.id === this.selectedJournalId)
      if (!journal) { return [] }
      return journal.students.filter((s) => s.active)
    },

    customNameLines(): string[] {
      return this.customNames
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
    },

    wheelItems(): string[] {
      if (this.customNameLines.length > 0) { return this.customNameLines }
      return buildUniqueNameLabels(this.students)
    },
  },

  watch: {
    "studentWheelStore.modalOpen"(isOpen: boolean) {
      if (isOpen) {
        this.fetchJournals()
        this.restoreJournalFromStorage()
        this.$nextTick(() => {
          this.buildWheel()
          this.startScrimClipObserver()
        })
      } else {
        this.stopScrimClipObserver()
        this.reset()
      }
    },

    selectedJournalId(newId: number | null) {
      if (newId !== null) {
        storage.setStudentWheelJournalId(newId)
        this.customNames = ""
      }
      this.$nextTick(() => this.buildWheel())
    },

    wheelItems() {
      if (this.studentWheelStore.modalOpen) {
        this.$nextTick(() => this.buildWheel())
      }
    },
  },

  methods: {
    fetchJournals() {
      if (this.eklaseStore.journals.length > 0) {
        this.loadingJournals = false
        return
      }

      this.loadingJournals = true
      this.eklaseStore.fetchJournals({
        successCallback: () => {
          this.loadingJournals = false
          this.restoreJournalFromStorage()
        },
        errorCallback: () => {
          this.loadingJournals = false
        },
      })
    },

    restoreJournalFromStorage() {
      if (this.selectedJournalId) { return }
      const id = storage.getStudentWheelJournalId()
      if (!id) { return }
      if (this.eklaseStore.journals.some((j) => j.id === id)) {
        this.selectedJournalId = id
      }
    },

    buildWheel() {
      const container = this.$refs.wheelContainer as HTMLElement | null
      if (!container) { return }

      if (this.wheelInstance) {
        this.wheelInstance.remove()
        this.wheelInstance = null
      }

      const items = this.wheelItems.length > 0
        ? this.wheelItems.map((label) => ({ label }))
        : buildPlaceholderItems()

      this.wheelInstance = new Wheel(container, {
        items,
        itemLabelRadiusMax: 0.35,
        itemLabelFontSizeMax: 16,
        itemLabelAlign: "right",
        borderColor: "#FFFFFF",
        borderWidth: 2,
        lineColor: "#FFFFFF",
        lineWidth: 1,
        radius: 0.9,
        isInteractive: false,
        itemBackgroundColors: WHEEL_COLORS,
        itemLabelColors: ["#221B00"],
        pointerAngle: 0,
      })

      this.isSpinning = false
      this.selectedStudent = null
    },

    spin() {
      if (!this.wheelInstance || this.wheelItems.length === 0 || this.isSpinning) { return }

      const winnerIndex = Math.floor(Math.random() * this.wheelItems.length)
      const winnerLabel = this.wheelItems[winnerIndex]

      this.isSpinning = true
      this.selectedStudent = null

      this.wheelInstance.onRest = () => {
        if (this.customNameLines.length > 0) {
          this.selectedStudent = winnerLabel
        } else {
          this.selectedStudent = findFullName(this.students, winnerLabel)
        }
        this.isSpinning = false
        this.fireConfetti()
      }

      this.wheelInstance.spinToItem(winnerIndex, 3000, true, 5, 1)
    },

    clipScrimAroundButton() {
      const scrim = document.querySelector(".student-wheel-dialog .v-overlay__scrim") as HTMLElement
      const btn = document.querySelector(".student-wheel-picker-btn") as HTMLElement
      if (!scrim || !btn) { return }

      const rect = btn.getBoundingClientRect()
      const r = Math.min(25, rect.width / 2, rect.height / 2)
      const { left: x, top: y, width: w, height: h } = rect

      const hole = `M ${x + r} ${y} A ${r} ${r} 0 0 0 ${x} ${y + r} L ${x} ${y + h - r} A ${r} ${r} 0 0 0 ${x + r} ${y + h} L ${x + w - r} ${y + h} A ${r} ${r} 0 0 0 ${x + w} ${y + h - r} L ${x + w} ${y + r} A ${r} ${r} 0 0 0 ${x + w - r} ${y} Z`
      const outer = "M 0 0 L 100000 0 L 100000 100000 L 0 100000 Z"

      scrim.style.clipPath = `path('${outer} ${hole}')`
    },

    startScrimClipObserver() {
      this.stopScrimClipObserver()
      const handler = () => this.clipScrimAroundButton()
      this.clipScrimHandler = handler
      this.scrimClipObserver = new ResizeObserver(handler)
      this.scrimClipObserver.observe(document.documentElement)
      window.addEventListener("resize", handler)
    },

    stopScrimClipObserver() {
      if (this.scrimClipObserver) {
        this.scrimClipObserver.disconnect()
        this.scrimClipObserver = null
      }
      if (this.clipScrimHandler) {
        window.removeEventListener("resize", this.clipScrimHandler)
        this.clipScrimHandler = null
      }
    },

    fireConfetti() {
      const somaColors = ["#F9D805", "#FFF1AE", "#221B00", "#FFFFFF"]
      const burst = { particleCount: 80, spread: 70, colors: somaColors, zIndex: 2500 }

      confetti({ ...burst, angle: 60, origin: { x: 0, y: 0.6 } })
      confetti({ ...burst, angle: 120, origin: { x: 1, y: 0.6 } })
    },

    reset() {
      if (this.wheelInstance) {
        this.wheelInstance.remove()
        this.wheelInstance = null
      }
      this.selectedJournalId = null
      this.customNames = ""
      this.isSpinning = false
      this.selectedStudent = null
    },
  },
}
</script>

<style lang="sass" scoped>
.student-wheel-dialog :deep(.v-overlay__scrim)
  background: rgba(255, 255, 255, 0.75) !important
  opacity: 1 !important

  &::before
    content: ""
    position: absolute
    inset: 0
    backdrop-filter: blur(8px)
    z-index: -1

.student-wheel-dialog :deep(.v-overlay__content)
  background: transparent !important
  pointer-events: none
.student-wheel-overlay
  width: 100vw
  height: 100vh
  display: flex
  flex-direction: column
  align-items: center
  pointer-events: none

.student-wheel-toolbar
  position: absolute
  top: 72px
  right: 16px
  display: flex
  flex-direction: column
  align-items: stretch
  gap: 12px
  z-index: 1
  pointer-events: auto

.student-wheel-toolbar-row
  display: flex
  align-items: center
  gap: 12px

.student-wheel-toolbar-separator
  display: flex
  align-items: center
  gap: 8px
  color: rgba(0, 0, 0, 0.5)
  font-size: 14px

  .line
    flex: 1
    height: 1px
    background: rgba(0, 0, 0, 0.15)

.student-wheel-custom-names
  width: 100%

  :deep(.v-field)
    background: #FFFFFF

:deep(.student-wheel-journal-select)
  min-width: 256px
  .v-field__field, .v-field__append-inner
    height: 44px
  .v-field__input
    font-weight: 400
    font-size: 16px
    line-height: 44px
    padding-top: 0
    padding-bottom: 0
    min-height: 44px

.student-wheel-spin-btn
  height: 44px
  text-transform: none
  font-size: 16px !important
  line-height: 20px !important
  letter-spacing: 0.1px

  &.v-btn--disabled
    background: rgba(0, 0, 0, 0.12) !important
    color: rgba(0, 0, 0, 0.38) !important

.student-wheel-center
  flex: 1
  display: flex
  align-items: center
  justify-content: center
  position: relative
  pointer-events: auto
  cursor: pointer

.student-wheel-container
  width: 620px
  height: 620px

.student-wheel-pointer-top
  position: absolute
  top: calc(50% - 320px)
  left: 50%
  transform: translateX(-50%)
  width: 0
  height: 0
  border-left: 14px solid transparent
  border-right: 14px solid transparent
  border-top: 22px solid #221B00
  z-index: 1

.student-wheel-result
  position: absolute
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)
  z-index: 2
  pointer-events: none

.student-wheel-result-name
  background: #221B00
  color: #F9D805
  font-size: 28px
  font-weight: 700
  padding: 14px 32px
  border-radius: 50px
  white-space: nowrap
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3)
</style>
